import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import styles from './intake.module.scss';
import handsIcon from './hands.ico';
import { site_captcha_key, CLUSTER } from '../../constants';
import { useTrackingContext } from '../../hooks/useTrackingContext';
import { useAppStoreUrl } from '../../hooks/useAppStoreUrl';
import { TrackingContext } from '../../context/TrackingContext';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import ReCAPTCHA from 'react-google-recaptcha';

const BASE_PATH = CLUSTER !== 'dev' ? '/ai-ui' : '';

// ── Quiz data ──────────────────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: 1,
    label: 'Assessment 1 of 4',
    question: 'What pulls you in?',
    type: 'image',
    options: [
      { key: 'a', text: 'The hallway', img: `${BASE_PATH}/static/images/conjure/intake/1.png` },
      { key: 'b', text: 'The broken mirror', img: `${BASE_PATH}/static/images/conjure/intake/2.png` },
      { key: 'c', text: 'The chairs', img: `${BASE_PATH}/static/images/conjure/intake/3.png` },
      { key: 'd', text: 'The open door', img: `${BASE_PATH}/static/images/conjure/intake/4.png` },
    ],
  },
  {
    id: 2,
    label: 'Assessment 2 of 4',
    question: 'What truly terrifies you?',
    preCopy: 'Fear is a basic human instinct. Don\'t overthink the following question.',
    instruction: 'Plot yourself on the matrix.',
    type: 'matrix',
    axes: {
      top: 'Things Of this world',
      bottom: 'Things Not of this world',
      left: 'Things that you can see',
      right: 'Things that remain hidden',
    },
    quadrants: {
      topLeft: {
        name: 'Visible + of this world',
        description: 'A stalker, a fire, a predator. Something with a face. Something that moves toward you and doesn\'t stop.',
      },
      topRight: {
        name: 'Hidden + of this world',
        description: 'Cancer, what\'s in the walls, what\'s in the basement. The threat that doesn\'t announce itself. It\'s already there.',
      },
      bottomLeft: {
        name: 'Visible + not of this world',
        description: 'Apparitions, sightings, manifestations. You saw it. You know what you saw. Nobody else was looking.',
      },
      bottomRight: {
        name: 'Hidden + not of this world',
        description: 'The watcher, the dimensional, the thing behind you. It doesn\'t need a shape. You feel it anyway.',
      },
    },
  },
  {
    id: 3,
    label: 'Assessment 3 of 4',
    question: 'Have you ever been in a room where the energy suddenly shifts?',
    type: 'choice',
    options: [
      { key: 'a', text: 'Yes, and I left immediately.' },
      { key: 'b', text: 'Yes, but I just sat in discomfort.' },
      { key: 'c', text: 'Maybe once or twice. It wasn\'t scary, it just felt unnatural.' },
      { key: 'd', text: 'Rooms don\'t have energy. I don\'t really get this question.' },
    ],
  },
  {
    id: 4,
    label: 'Assessment 4 of 4',
    question: 'What unsettles you most?',
    instruction: 'Move the dial to where you honestly land.',
    type: 'slider',
    leftLabel: 'Something that was here before language even existed',
    rightLabel: 'Something that\'s never existed before and doesn\'t have a name yet',
    readings: [
      { min: 1, max: 15, text: 'The ancient kind. Something patient. It was here before us and it will be here after. It doesn\'t need to hurry.' },
      { min: 16, max: 30, text: 'Old darkness. The house that remembers. The object that passed through too many hands. Land that absorbed something and never let it go.' },
      { min: 31, max: 45, text: 'Something in between \u2014 old enough to have roots, close enough that you can still feel it.' },
      { min: 46, max: 55, text: 'Both. Equally. You don\'t get to choose which one finds you first.' },
      { min: 56, max: 70, text: 'Something recent enough to have a shape you almost recognize. A system. A signal. Something that learned how to want things.' },
      { min: 71, max: 85, text: 'The emerging kind. Something that\'s growing. That didn\'t exist ten years ago and will be unrecognizable in ten more.' },
      { min: 86, max: 100, text: 'Something with no name yet. It doesn\'t fit any category you have. That\'s the part that unsettles you most.' },
    ],
  },
];

// Scoring maps — Q4 slider does not score, Q2 matrix scores by quadrant, Q1 image scores
const SCORING_MAP = {
  1: { a: 3, b: 2, c: 2, d: 3 },
  3: { a: 3, b: 3, c: 2, d: 0 },
};

// Q2 matrix scoring by quadrant
const MATRIX_SCORING = {
  topLeft: 2,
  topRight: 3,
  bottomLeft: 2,
  bottomRight: 3,
};

// Cumulative max possible score at each step (Q4 doesn't score)
const MAX_SCORE_AT_STEP = { 1: 3, 2: 6, 3: 9, 4: 9 };

const SIGNAL_LEVELS = [
  { min: 0, max: 0, label: 'NO SIGNAL', bars: 0 },
  { min: 0.01, max: 0.24, label: 'WEAK', bars: 1 },
  { min: 0.25, max: 0.49, label: 'FAIR', bars: 2 },
  { min: 0.50, max: 0.74, label: 'GOOD', bars: 3 },
  { min: 0.75, max: 1, label: 'STRONG', bars: 4 },
];

// Q4 slider theme tags
const sliderThemeTag = (val) => {
  if (val <= 30) return 'paranormal';
  if (val <= 70) return 'ambiguity';
  return 'alien';
};

const FEAR_QUADRANT_MAP = {
  topLeft: 'worldly_visible',
  topRight: 'worldly_hidden',
  bottomLeft: 'otherworldly_visible',
  bottomRight: 'otherworldly_hidden',
};

// Archetypes — matched to intake2 style with amber terminal ASCII art
const ARCHETYPES = {
  closed_eye: {
    name: 'The Closed Eye',
    signalStrength: 22,
    scoreRange: [0, 4],
    description: 'You trust what can be verified. You make good decisions because you don\'t let things cloud your judgment. But here\'s what\'s also true: you took this assessment. Something made you click. You don\'t seem ready. Proceed with caution — the seekers may not accept you.',
    art: [
      '  ╔══════════════════════════╗  ',
      '  ║                          ║  ',
      '  ║    ━━━━━━━━━━━━━━━━━     ║  ',
      '  ║  ╱   ─ ─ ─ ─ ─ ─ ─ ╲   ║  ',
      '  ║ │  ─ ─ ─ ─ ─ ─ ─ ─  │  ║  ',
      '  ║  ╲   ─ ─ ─ ─ ─ ─ ─ ╱   ║  ',
      '  ║    ━━━━━━━━━━━━━━━━━     ║  ',
      '  ║                          ║  ',
      '  ║    ┊ signal blocked ┊    ║  ',
      '  ╚══════════════════════════╝  ',
    ],
  },
  looked_away: {
    name: 'The One Who Looked Away',
    signalStrength: 58,
    scoreRange: [5, 6],
    description: 'You feel things before you can explain them. You notice when a room has changed. You\'ve had moments you filed under coincidence because the alternative required more than you were ready to give.',
    art: [
      '  ╔══════════════════════════╗  ',
      '  ║       ╭───╮              ║  ',
      '  ║       │ ◉ │  ╲           ║  ',
      '  ║       ╰─┬─╯    ╲        ║  ',
      '  ║         │        ·       ║  ',
      '  ║        ╱│╲      ·        ║  ',
      '  ║       ╱ │ ╲    ·         ║  ',
      '  ║         │      ◌         ║  ',
      '  ║        ╱ ╲               ║  ',
      '  ║   ┊ looked away ┊       ║  ',
      '  ╚══════════════════════════╝  ',
    ],
  },
  at_the_door: {
    name: 'The One at the Door',
    signalStrength: 74,
    scoreRange: [7, 8],
    description: 'You live with open questions comfortably. You\'re drawn to the liminal — the pause before someone answers, the hallway rather than the room. You believe in more than you\'ll readily admit. Not out of embarrassment — out of precision.',
    art: [
      '  ╔══════════════════════════╗  ',
      '  ║    ┌──────┬──────┐      ║  ',
      '  ║    │▓▓▓▓▓▓│      │      ║  ',
      '  ║    │▓▓▓▓▓▓│  ░   │      ║  ',
      '  ║    │▓▓▓▓▓▓│      │      ║  ',
      '  ║    │▓▓▓▓▓▓│ ◌    │      ║  ',
      '  ║    │▓▓▓▓▓▓│      │      ║  ',
      '  ║    ├──────┘      │      ║  ',
      '  ║    └─────────────┘      ║  ',
      '  ║  ┊ the door is open ┊   ║  ',
      '  ╚══════════════════════════╝  ',
    ],
  },
  already_knows: {
    name: 'The One Who Already Knows',
    signalStrength: 92,
    scoreRange: [9, 12],
    description: 'You notice what others don\'t — not because you\'re looking for it, but because it finds you first. You\'ve developed a particular kind of loneliness: the loneliness of knowing something and having nowhere to put it.',
    art: [
      '  ╔══════════════════════════╗  ',
      '  ║      ╭━━━━━━━━━╮        ║  ',
      '  ║    ╱               ╲    ║  ',
      '  ║  ╱    ╭─────────╮    ╲  ║  ',
      '  ║ │    │     ◉     │    │ ║  ',
      '  ║  ╲    ╰─────────╯    ╱  ║  ',
      '  ║    ╲               ╱    ║  ',
      '  ║      ╰━━━━━━━━━╯        ║  ',
      '  ║                          ║  ',
      '  ║   ┊ summons issued ┊    ║  ',
      '  ╚══════════════════════════╝  ',
    ],
  },
};

const PROCESSING_FILES = [
  'Reading signal.dat',
  'Parsing frequency.cfg',
  'Loading resonance.tbl',
  'Calibrating channel.sys',
  'Decoding pattern.bin',
  'Analyzing waveform.raw',
  'Mapping coordinates.hex',
  'Verifying alignment.key',
  'Compiling assessment.out',
];

const isValidEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
};

// ── Typewriter hook ────────────────────────────────────────────────────────────

function useTypewriter(text, speed = 30, trigger = true) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!trigger) { setDisplayed(''); setDone(false); return; }
    setDisplayed(''); setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, trigger]);

  return { displayed, done };
}

function TypedLine({ text, className, as: Tag = 'div', speed = 25, onDone }) {
  const { displayed, done } = useTypewriter(text, speed);
  useEffect(() => { if (done && onDone) onDone(); }, [done, onDone]);
  return (
    <Tag className={className}>
      {displayed}
      {!done && <span className={styles.cursor}>\u2588</span>}
    </Tag>
  );
}

function TypedOption({ text, delay = 0, onReady, onClick, className }) {
  const [visible, setVisible] = useState(false);
  const { displayed, done } = useTypewriter(text, 18, visible);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => { if (done && onReady) onReady(); }, [done, onReady]);

  if (!visible) return null;
  return (
    <button className={className} onClick={done ? onClick : undefined} style={{ opacity: done ? 1 : 0.6, cursor: done ? 'pointer' : 'default' }}>
      {displayed}
      {!done && <span className={styles.cursor}>\u2588</span>}
    </button>
  );
}

// ── Slider component (retro terminal gauge) ────────────────────────────────────

function SliderQuestion({ question, onAnswer }) {
  const [value, setValue] = useState(50);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [questionReady, setQuestionReady] = useState(false);
  const trackRef = useRef(null);

  const reading = useMemo(() => {
    if (value === null) return null;
    return question.readings.find((r) => value >= r.min && value <= r.max);
  }, [value, question.readings]);

  const updateValue = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setValue(Math.max(1, Math.round(pct * 100)));
    setHasInteracted(true);
  }, []);

  const handlePointerDown = useCallback((e) => {
    setDragging(true);
    updateValue(e.clientX);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [updateValue]);

  const handlePointerMove = useCallback((e) => {
    if (!dragging) return;
    updateValue(e.clientX);
  }, [dragging, updateValue]);

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  return (
    <div className={styles.questionBlock}>
      <TypedLine text={question.label} className={styles.label} speed={20} />
      <TypedLine
        text={question.question}
        className={styles.questionText}
        speed={20}
        onDone={() => setTimeout(() => setQuestionReady(true), 200)}
      />
      {questionReady && (
        <div className={styles.sliderContainer}>
          {question.instruction && (
            <TypedLine text={question.instruction} className={styles.sliderInstruction} speed={15} />
          )}
          <div className={styles.sliderLabels}>
            <span className={styles.sliderLabelLeft}>{question.leftLabel}</span>
            <span className={styles.sliderLabelRight}>{question.rightLabel}</span>
          </div>
          <div
            ref={trackRef}
            className={styles.sliderTrack}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div className={styles.sliderFill} style={{ width: value ? `${value}%` : '0%' }} />
            {value !== null && (
              <div className={styles.sliderThumb} style={{ left: `${value}%` }}>
                <div className={styles.sliderThumbInner} />
              </div>
            )}
            {/* Tick marks */}
            <div className={styles.sliderTicks}>
              {[0, 25, 50, 75, 100].map((t) => (
                <div key={t} className={styles.sliderTick} style={{ left: `${t}%` }} />
              ))}
            </div>
          </div>
          <div className={styles.sliderValue}>
            {value !== null ? `${value}/100` : '\u2014'}
          </div>
          {reading && (
            <div className={styles.sliderReading}>
              <TypedLine text={reading.text} speed={10} />
            </div>
          )}
          {hasInteracted && (
            <button className={styles.continueButton} onClick={() => onAnswer(question.id, value)}>
              &gt; CONTINUE
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Matrix component (2x2 fear plot) ───────────────────────────────────────────

function MatrixQuestion({ question, onAnswer }) {
  const [dot, setDot] = useState(null); // { x: 0-1, y: 0-1 }
  const [questionReady, setQuestionReady] = useState(false);
  const [preCopyDone, setPreCopyDone] = useState(!question.preCopy);
  const matrixRef = useRef(null);
  const revealRef = useRef(null);

  const quadrant = useMemo(() => {
    if (!dot) return null;
    const row = dot.y < 0.5 ? 'top' : 'bottom';
    const col = dot.x < 0.5 ? 'Left' : 'Right';
    return `${row}${col}`;
  }, [dot]);

  const quadrantData = quadrant ? question.quadrants[quadrant] : null;

  // Scroll reveal into view when quadrant is selected
  useEffect(() => {
    if (quadrantData && revealRef.current) {
      setTimeout(() => {
        revealRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [quadrantData]);

  const placeDot = useCallback((clientX, clientY) => {
    const el = matrixRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    setDot({ x, y });
  }, []);

  const handlePointerDown = useCallback((e) => {
    placeDot(e.clientX, e.clientY);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [placeDot]);

  const handlePointerMove = useCallback((e) => {
    if (e.buttons === 0) return;
    placeDot(e.clientX, e.clientY);
  }, [placeDot]);

  return (
    <div className={styles.questionBlock}>
      <TypedLine text={question.label} className={styles.label} speed={20} />
      {question.preCopy && !preCopyDone && (
        <TypedLine
          text={question.preCopy}
          className={styles.preCopy}
          speed={15}
          onDone={() => setTimeout(() => setPreCopyDone(true), 400)}
        />
      )}
      {preCopyDone && (
        <>
          <TypedLine
            text={question.question}
            className={styles.questionText}
            speed={20}
            onDone={() => setTimeout(() => setQuestionReady(true), 200)}
          />
          {questionReady && (
            <div className={styles.matrixContainer}>
              {question.instruction && (
                <TypedLine text={question.instruction} className={styles.sliderInstruction} speed={15} />
              )}
              <div className={styles.matrixAxisTop}>{question.axes.top}</div>
              <div className={styles.matrixRow}>
                <div className={styles.matrixAxisLeft}>{question.axes.left}</div>
                <div
                  ref={matrixRef}
                  className={styles.matrixGrid}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                >
                  {/* Crosshair lines */}
                  <div className={styles.matrixCrossH} />
                  <div className={styles.matrixCrossV} />
                  {/* Dot */}
                  {dot && (
                    <div
                      className={styles.matrixDot}
                      style={{ left: `${dot.x * 100}%`, top: `${dot.y * 100}%` }}
                    />
                  )}
                </div>
                <div className={styles.matrixAxisRight}>{question.axes.right}</div>
              </div>
              <div className={styles.matrixAxisBottom}>{question.axes.bottom}</div>
              {quadrantData && (
                <div className={styles.matrixReveal} ref={revealRef}>
                  <div className={styles.matrixQuadrantName}>{quadrantData.name}</div>
                  <TypedLine text={quadrantData.description} speed={10} />
                </div>
              )}
              {dot && (
                <button className={styles.continueButton} onClick={() => onAnswer(question.id, quadrant)}>
                  &gt; CONTINUE
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── DOS Progress Bar ───────────────────────────────────────────────────────────

function DOSProgressBar({ onComplete, duration = 4000 }) {
  const [percent, setPercent] = useState(0);
  const [fileIndex, setFileIndex] = useState(0);

  useEffect(() => {
    const steps = 100;
    const stepTime = duration / steps;
    let current = 0;
    let completionTimeout;
    const interval = setInterval(() => {
      current++;
      setPercent(current);
      const fi = Math.min(Math.floor((current / 100) * PROCESSING_FILES.length), PROCESSING_FILES.length - 1);
      setFileIndex(fi);
      if (current >= steps) {
        clearInterval(interval);
        completionTimeout = setTimeout(() => onComplete(), 400);
      }
    }, stepTime);
    return () => {
      clearInterval(interval);
      clearTimeout(completionTimeout);
    };
  }, [duration, onComplete]);

  return (
    <div className={styles.processingBlock}>
      <div className={styles.processingLabel}>PROCESSING SIGNAL</div>
      <div className={styles.processingText}>Compiling assessment data...</div>
      <div className={styles.dosProgressContainer}>
        <div className={styles.dosProgressPercent}>{percent}% complete</div>
        <div className={styles.dosProgressTrack}>
          <div className={styles.dosProgressBar} style={{ width: `${percent}%` }} />
        </div>
      </div>
      <div className={styles.processingFile}>{PROCESSING_FILES[fileIndex]}</div>
    </div>
  );
}

// ── Signal strength indicator (fixed top-right during quiz) ────────────────────

function SignalIndicator({ score, currentStep, recalibrating }) {
  const maxSoFar = MAX_SCORE_AT_STEP[Math.min(currentStep, 4)] || 3;
  const ratio = maxSoFar > 0 ? score / maxSoFar : 0;

  // Determine how many bars (0-4) and the label
  let activeBars = 0;
  let label = 'NO SIGNAL';
  if (score === 0 && currentStep === 1) {
    // Haven't answered yet
    activeBars = 0;
    label = 'NO SIGNAL';
  } else {
    const level = SIGNAL_LEVELS.find((l) => ratio >= l.min && ratio <= l.max) || SIGNAL_LEVELS[0];
    activeBars = level.bars;
    label = level.label;
  }

  return (
    <div className={`${styles.signalIndicator} ${recalibrating ? styles.signalRecalibrating : ''}`}>
      <div className={styles.signalBars}>
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={`${styles.signalBar} ${styles[`signalBar${bar}`]} ${bar <= activeBars ? styles.signalBarActive : ''}`}
          />
        ))}
      </div>
      <div className={styles.signalLabelText}>
        {recalibrating ? 'RECALIBRATING...' : label}
      </div>
    </div>
  );
}

// ── Error boundary ─────────────────────────────────────────────────────────────

class IntakeErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[Conjure Intake] client-side exception:', error, info);
  }
  render() { return this.state.error ? null : this.props.children; }
}

// ── Main quiz component ────────────────────────────────────────────────────────

function IntakeQuiz() {
  // Screens: -1=scanning, 0=entry, 1-8=questions, 9=calibration, 10=result
  const [currentStep, setCurrentStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [entryReady, setEntryReady] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [processingDone, setProcessingDone] = useState(false);
  const [scanDone, setScanDone] = useState(false);
  const [recalibrating, setRecalibrating] = useState(false);
  const prevScoreRef = useRef(0);

  // Android waitlist form state
  const [email, setEmail] = useState('');
  const [phoneValue, setPhoneValue] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const recaptchaRef = useRef(null);

  const containerRef = useRef(null);
  const endRef = useRef(null);
  const seekerCount = useMemo(() => 11247 + Math.floor(Math.random() * 300), []);

  // CET tracking
  const {
    trackContentAction,
    trackExternalLink,
    trackPageview,
    attachTimeSpentHandler,
  } = useTrackingContext({ isConjure: true });

  const appStoreUrl = useAppStoreUrl('intake-quiz');

  // ── Derived scoring ──

  const score = useMemo(() => {
    let total = 0;
    Object.entries(answers).forEach(([qId, ansVal]) => {
      const id = parseInt(qId, 10);
      // Choice-scored questions
      const map = SCORING_MAP[id];
      if (map && typeof ansVal === 'string') { total += map[ansVal] || 0; return; }
      // Q4 slider (unsettled_by) — does NOT score
      // Q2 matrix
      if (id === 2 && typeof ansVal === 'string') { total += MATRIX_SCORING[ansVal] || 0; return; }
    });
    return total;
  }, [answers]);

  const archetype = useMemo(() => {
    if (score >= 9) return ARCHETYPES.already_knows;
    if (score >= 7) return ARCHETYPES.at_the_door;
    if (score >= 5) return ARCHETYPES.looked_away;
    return ARCHETYPES.closed_eye;
  }, [score]);

  const archetypeKey = useMemo(() => {
    if (score >= 9) return 'already_knows';
    if (score >= 7) return 'at_the_door';
    if (score >= 5) return 'looked_away';
    return 'closed_eye';
  }, [score]);

  const themeTag = useMemo(() => {
    const q4 = answers[4];
    return typeof q4 === 'number' ? sliderThemeTag(q4) : null;
  }, [answers]);

  const fearQuadrant = useMemo(() => {
    const q2 = answers[2];
    return q2 ? FEAR_QUADRANT_MAP[q2] || null : null;
  }, [answers]);

  const communityTag = null;

  // Auto-detect device type
  const deviceType = useMemo(() => {
    if (typeof window === 'undefined') return 'desktop';
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
    if (/android/i.test(ua)) return 'android';
    return 'desktop';
  }, []);

  // ── Effects ──

  // Auto-advance scanning screen
  useEffect(() => {
    if (currentStep !== -1) return;
    const t = setTimeout(() => {
      setScanDone(true);
      setTimeout(() => setCurrentStep(0), 400);
    }, 3000);
    return () => clearTimeout(t);
  }, [currentStep]);

  // Pageview + time spent on mount
  useEffect(() => {
    // Force html/body black for iOS Safari toolbar + overscroll
    document.documentElement.style.background = '#000';
    document.body.style.background = '#000';
    trackPageview({ context_page_type: 'utility', context_page_id: 'intake', destination: 'conjure', page_edition: 'en-us' });
    attachTimeSpentHandler({
      unit_type: 'component',
      unit_name: 'conjure_intake',
      context_page_type: 'utility',
      context_page_id: 'intake',
      destination: 'conjure',
      page_edition: 'en-us',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entry typewriter trigger
  useEffect(() => {
    if (currentStep === 0) {
      const t = setTimeout(() => setEntryReady(true), 200);
      return () => clearTimeout(t);
    }
  }, [currentStep]);

  // Scroll to top of active question on step changes
  useEffect(() => {
    if (containerRef.current && currentStep > 0) {
      setTimeout(() => {
        containerRef.current.scrollTop = 0;
      }, 150);
    }
  }, [currentStep, processingDone]);

  // Auto-scroll forward as content grows within a question
  useEffect(() => {
    const container = containerRef.current;
    if (!container || currentStep <= 0) return;
    let rafId = null;
    const observer = new MutationObserver(() => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        // Scroll so the bottom of new content is visible
        container.scrollTop = container.scrollHeight - container.clientHeight;
      });
    });
    observer.observe(container, { childList: true, subtree: true, characterData: true });
    return () => { observer.disconnect(); if (rafId) cancelAnimationFrame(rafId); };
  }, [currentStep]);

  // Reset showOptions when step changes
  useEffect(() => {
    if (currentStep >= 1 && currentStep <= 4) setShowOptions(false);
  }, [currentStep]);

  // Signal recalibration blink when score changes
  useEffect(() => {
    if (currentStep < 1) return;
    if (score !== prevScoreRef.current) {
      prevScoreRef.current = score;
      setRecalibrating(true);
      const t = setTimeout(() => setRecalibrating(false), 800);
      return () => clearTimeout(t);
    }
  }, [score, currentStep]);

  // Fire quiz complete when processing finishes
  useEffect(() => {
    if (!processingDone) return;
    trackContentAction({
      unit_type: 'component',
      unit_name: 'conjure_intake',
      action_type: 'quiz_result',
      action_value: 'conjure_intake_submission',
      item_type: 'button',
      item_name: `${archetypeKey}_${deviceType}`,
      conjure_score: score,
      conjure_archetype: archetypeKey,
      conjure_signal_strength: archetype.signalStrength,
      conjure_theme: themeTag,
      conjure_fear_quadrant: fearQuadrant,
      conjure_community: communityTag,
      conjure_device: deviceType,
      conjure_unsettled_by: typeof answers[4] === 'number' ? answers[4] : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingDone]);

  const handleProcessingComplete = useCallback(() => setProcessingDone(true), []);

  // ── Handlers ──

  const handleBeginAssessment = () => {
    trackContentAction({
      unit_type: 'component',
      unit_name: 'conjure_intake',
      subunit_type: 'component',
      subunit_name: 'quiz_entry',
      item_type: 'button',
      item_name: 'begin_assessment',
      action_type: 'select',
      action_value: 'start_quiz',
    });
    setCurrentStep(1);
  };

  const handleAnswer = (questionId, answerValue) => {
    const question = QUESTIONS[questionId - 1];

    // CET tracking for all answer types
    let itemName;
    if (typeof answerValue === 'number') {
      itemName = `q${questionId}_slider_${answerValue}`;
    } else {
      itemName = `q${questionId}_${answerValue}`;
    }

    trackContentAction({
      unit_type: 'component',
      unit_name: 'conjure_intake',
      subunit_type: 'component',
      subunit_name: 'quiz_question',
      item_type: 'button',
      item_name: itemName,
      position_in_unit: questionId,
      action_type: 'select',
      action_value: question.label.toLowerCase().replace(/\s+/g, '_'),
    });

    setAnswers((prev) => ({ ...prev, [questionId]: answerValue }));

    setTimeout(() => {
      // Questions 1-4, then processing/result at step 5
      setCurrentStep(questionId < 4 ? questionId + 1 : 5);
    }, 400);
  };

  const handleAppStoreClick = () => {
    trackExternalLink({
      unit_type: 'component',
      unit_name: 'conjure_intake',
      subunit_type: 'component',
      subunit_name: 'quiz_result',
      item_type: 'button',
      item_name: 'open_the_channel',
      target_content_url: appStoreUrl,
    });
  };

  const handleWaitlistSubmit = async () => {
    if (!isValidEmail(email)) {
      setSubmitError('Invalid frequency format. Please enter a valid email.');
      return;
    }
    if (phoneValue && !isValidPhoneNumber(phoneValue)) {
      setSubmitError('Invalid signal pattern. Check number or leave blank.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      let token;
      if (site_captcha_key && recaptchaRef.current?.executeAsync) {
        token = await recaptchaRef.current.executeAsync();
        recaptchaRef.current?.reset();
      }
      const body = {
        brand: 'buzzfeed',
        subscriptions: ['buzzfeed_email_conjure_android'],
        source: 'conjure-intake-quiz',
        'g-recaptcha-response': token,
        email: String(email).trim(),
      };
      if (phoneValue) body.phone_number = phoneValue;

      const response = await fetch('/newsletters/api/subhub/v1/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errBody;
        try { errBody = await response.json(); } catch (e) { /* ignore */ }
        // eslint-disable-next-line no-console
        console.error('[Conjure Intake] Waitlist signup failed', response.status, errBody);
        setSubmitError(errBody?.error || 'Signal disrupted. Please try again.');
        return;
      }
      trackContentAction({
        unit_type: 'component',
        unit_name: 'conjure_intake',
        subunit_type: 'component',
        subunit_name: 'quiz_result',
        item_type: 'button',
        item_name: 'join_waitlist',
        action_type: 'post',
        action_value: 'android_waitlist_signup',
      });
      setSubmitSuccess(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Conjure Intake] Waitlist signup errored', err);
      setSubmitError('Signal disrupted. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Renderers ──

  const renderScanning = () => (
    <div className={`${styles.scanningScreen} ${scanDone ? styles.scanningFadeOut : ''}`}>
      <div className={styles.scanLine} />
      <TypedLine text="Scanning for signal" className={styles.scanningHeadline} as="h1" speed={50} />
      <div className={styles.scanningPulse} />
    </div>
  );

  const renderEntry = () => (
    <div className={styles.entryScreen}>
      {entryReady && (
        <>
          <img src={`${BASE_PATH}/static/images/conjure/intro-1.png`} alt="" className={styles.introImage} />
          <TypedLine text="You found us." className={styles.headline} as="h1" speed={60} onDone={() => setTimeout(() => setShowOptions(true), 300)} />
          {showOptions && (
            <>
              <TypedLine
                text="Before we grant full access to CONJURE&rsquo;s camera, we need to assess your frequency. Most people don't make it past this step."
                className={styles.subhead}
                as="p"
                speed={15}
              />
              <TypedLine
                text="Answer honestly. We'll know if you don't."
                className={styles.entryWarning}
                as="p"
                speed={20}
              />
              <button className={styles.beginButton} onClick={handleBeginAssessment}>
                &gt; BEGIN ASSESSMENT
              </button>
              <div className={styles.seekerFooter}>{seekerCount} seekers have entered</div>
            </>
          )}
        </>
      )}
    </div>
  );

  const renderAnsweredQuestion = (question) => {
    const val = answers[question.id];
    let displayText;
    if (question.type === 'slider') {
      displayText = `${val}/100`;
    } else if (question.type === 'matrix') {
      const qData = question.quadrants[val];
      displayText = qData ? qData.name : val;
    } else {
      const opt = question.options.find((o) => o.key === val);
      displayText = `[${String(val).toUpperCase()}] ${opt?.text || val}`;
    }
    return (
      <div className={styles.questionBlock} key={`answered-${question.id}`}>
        <div className={styles.label}>{question.label}</div>
        <div className={styles.questionText}>{question.question}</div>
        <div className={styles.answerConfirm}>&gt; SELECTED: {displayText}</div>
      </div>
    );
  };

  const renderActiveQuestion = (question) => {
    if (question.type === 'slider') {
      return <SliderQuestion key={`active-${question.id}`} question={question} onAnswer={handleAnswer} />;
    }
    if (question.type === 'matrix') {
      return <MatrixQuestion key={`active-${question.id}`} question={question} onAnswer={handleAnswer} />;
    }
    if (question.type === 'image') {
      return (
        <div className={styles.questionBlock} key={`active-${question.id}`}>
          <TypedLine text={question.label} className={styles.label} speed={20} />
          <TypedLine
            text={question.question}
            className={styles.questionText}
            speed={20}
            onDone={() => setTimeout(() => setShowOptions(true), 200)}
          />
          {showOptions && (
            <div className={styles.imageGrid}>
              {question.options.map((opt) => (
                <button
                  key={opt.key}
                  className={styles.imageGridCell}
                  onClick={() => handleAnswer(question.id, opt.key)}
                  type="button"
                >
                  <img src={opt.img} alt={opt.text} className={styles.imageGridImg} />
                  <span className={styles.imageGridLabel}>{opt.text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Multiple choice
    return (
      <div className={styles.questionBlock} key={`active-${question.id}`}>
        <TypedLine text={question.label} className={styles.label} speed={20} />
        <TypedLine
          text={question.question}
          className={styles.questionText}
          speed={20}
          onDone={() => setTimeout(() => setShowOptions(true), 200)}
        />
        {showOptions && (
          <div className={styles.options}>
            {question.options.map((opt, i) => (
              <TypedOption
                key={opt.key}
                text={`> [${opt.key.toUpperCase()}] ${opt.text}`}
                delay={i * 250}
                className={styles.optionButton}
                onClick={() => handleAnswer(question.id, opt.key)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderIOSResult = () => (
    <div className={styles.resultBlock}>
      <div className={styles.label}>CONJURE SIGNAL ASSESSMENT COMPLETE</div>
      <div className={styles.archetypeName}>
        <TypedLine text={archetype.name} as="h2" speed={40} />
      </div>
      <pre className={styles.resultArt}>
        {archetype.art.join('\n')}
      </pre>
      <div className={styles.signalMeter}>
        <div className={styles.signalMeterLabel}>Signal strength</div>
        <div className={styles.signalMeterTrack}>
          <div className={styles.signalMeterFill} style={{ width: `${archetype.signalStrength}%` }} />
        </div>
        <div className={styles.signalMeterValue}>{archetype.signalStrength}%</div>
      </div>
      <TypedLine text={archetype.description} className={styles.resultSubhead} as="p" speed={10} />
      <a
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.downloadButton}
        onClick={handleAppStoreClick}
      >
        &gt; ANSWER THE SUMMONS - DOWNLOAD CONJURE
      </a>
      <div className={styles.ctaSubtext}>Free on iOS</div>
      <div className={styles.privacyLink}>
        <a href="https://www.whatwillyouconjure.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">PRIVACY POLICY</a>
      </div>
    </div>
  );

  const renderDesktopResult = () => (
    <div className={styles.resultBlock}>
      <div className={styles.label}>SIGNAL ASSESSMENT COMPLETE</div>
      <div className={styles.archetypeName}>
        <TypedLine text={archetype.name} as="h2" speed={40} />
      </div>
      <pre className={styles.resultArt}>
        {archetype.art.join('\n')}
      </pre>
      <div className={styles.signalMeter}>
        <div className={styles.signalMeterLabel}>Signal strength</div>
        <div className={styles.signalMeterTrack}>
          <div className={styles.signalMeterFill} style={{ width: `${archetype.signalStrength}%` }} />
        </div>
        <div className={styles.signalMeterValue}>{archetype.signalStrength}%</div>
      </div>
      <TypedLine text={archetype.description} className={styles.resultSubhead} as="p" speed={10} />
      <a
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.downloadButton}
        onClick={handleAppStoreClick}
      >
        &gt; DOWNLOAD CONJURE TO OPEN THE CHANNEL
      </a>
      <div className={styles.ctaSubtext}>Available on iOS</div>
      <div className={styles.privacyLink}>
        <a href="https://www.whatwillyouconjure.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">PRIVACY POLICY</a>
      </div>
    </div>
  );

  const renderAndroidResult = () => (
    <div className={styles.resultBlock}>
      <div className={styles.label}>SIGNAL ASSESSMENT COMPLETE</div>
      <div className={styles.archetypeName}>
        <TypedLine text={archetype.name} as="h2" speed={40} />
      </div>
      <pre className={styles.resultArt}>
        {archetype.art.join('\n')}
      </pre>
      <div className={styles.signalMeter}>
        <div className={styles.signalMeterLabel}>Signal strength</div>
        <div className={styles.signalMeterTrack}>
          <div className={styles.signalMeterFill} style={{ width: `${archetype.signalStrength}%` }} />
        </div>
        <div className={styles.signalMeterValue}>{archetype.signalStrength}%</div>
      </div>
      <TypedLine text={archetype.description} className={styles.resultSubhead} as="p" speed={10} />
      <TypedLine
        text="The frequency hasn't opened on Android yet. You'll know when it does."
        className={styles.resultSubhead}
        as="p"
        speed={15}
      />
      {!submitSuccess ? (
        <div className={styles.waitlistForm}>
          {site_captcha_key && (
            <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={site_captcha_key} style={{ visibility: 'hidden' }} />
          )}
          <input
            className={styles.emailInput}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <PhoneInput
            className={styles.phoneInput}
            defaultCountry="US"
            flags={flags}
            value={phoneValue}
            onChange={setPhoneValue}
            placeholder="phone number (optional)"
            autoComplete="tel"
            countryCallingCodeEditable={false}
          />
          {submitError && <div className={styles.error}>{submitError}</div>}
          <button className={styles.submitButton} onClick={handleWaitlistSubmit} disabled={isSubmitting}>
            &gt; JOIN THE WAITLIST
          </button>
        </div>
      ) : (
        <div className={styles.successMessage}>&gt; FREQUENCY LOGGED. STAND BY.</div>
      )}
      <div className={styles.seekerFooter}>{seekerCount} others are waiting</div>
      <div className={styles.privacyLink}>
        <a href="https://www.whatwillyouconjure.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">PRIVACY POLICY</a>
      </div>
    </div>
  );

  // ── Layout ──

  const questionCount = 4; // Q1-Q4, no calibration step

  return (
    <div className={styles.intakePage}>
      <div className={styles.scanLinesOverlay} />
      <Head>
        <link rel="icon" href={handsIcon} sizes="any" />
        <link rel="shortcut icon" href={handsIcon} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        {/* eslint-disable-next-line @next/next/no-page-custom-font */}
        <link href="https://fonts.googleapis.com/css2?family=VT323&display=swap" rel="stylesheet" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content="#000000" media="(prefers-color-scheme: dark)" />
        <meta name="theme-color" content="#000000" media="(prefers-color-scheme: light)" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="description" content="Conjure - Signal Detected" />
        <meta property="og:description" content="Conjure - Signal Detected" />
        <title>Signal Detected | Conjure</title>
      </Head>
      <Script src="https://www.googletagmanager.com/gtag/js?id=G-5YECTVQGXN" strategy="afterInteractive" />
      <Script id="conjure-gtag" strategy="afterInteractive">{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-5YECTVQGXN');
      `}</Script>

      <div className={styles.terminalContainer} ref={containerRef}>
        {/* Scanning screen */}
        {currentStep === -1 && renderScanning()}

        {/* Entry screen */}
        {currentStep === 0 && renderEntry()}

        {/* Signal strength indicator — visible during questions */}
        {currentStep >= 1 && currentStep <= 4 && (
          <SignalIndicator score={score} currentStep={currentStep} recalibrating={recalibrating} />
        )}

        {/* Questions (with progress bar) */}
        {currentStep >= 1 && (
          <>
            {/* Progress bar — Q1-Q4 */}
            {currentStep <= 4 && (
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${(Math.min(currentStep, questionCount) / questionCount) * 100}%` }}
                />
              </div>
            )}

            {/* Answered questions history */}
            {QUESTIONS.slice(0, currentStep <= 4 ? currentStep - 1 : 4).map(
              (q) => answers[q.id] != null && renderAnsweredQuestion(q)
            )}

            {/* Active question (Q1-Q4) */}
            {currentStep <= 4 && renderActiveQuestion(QUESTIONS[currentStep - 1])}

            {/* Processing / Result screen */}
            {currentStep === 5 && !processingDone && (
              <DOSProgressBar onComplete={handleProcessingComplete} duration={4000} />
            )}
            {currentStep === 5 && processingDone && (
              deviceType === 'ios'
                ? renderIOSResult()
                : deviceType === 'android'
                  ? renderAndroidResult()
                  : renderDesktopResult()
            )}
          </>
        )}

        <div ref={endRef} />
      </div>

      <footer className={styles.privacyFooter}>
        <a href="https://www.whatwillyouconjure.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">PRIVACY POLICY</a>
      </footer>
    </div>
  );
}

// ── Page export ────────────────────────────────────────────────────────────────

export default function IntakePage() {
  const pageInfo = { context_page_type: 'utility', context_page_id: 'intake', destination: 'conjure', page_edition: 'en-us' };
  return (
    <TrackingContext.Provider value={pageInfo}>
      <IntakeErrorBoundary>
        <IntakeQuiz />
      </IntakeErrorBoundary>
    </TrackingContext.Provider>
  );
}
